import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * URL as state (docs/dashboard-ux-proposal.md: "every drawer and filter is a URL").
 *
 * A search param keeps the page mounted while a drawer opens over it — the
 * background-location trick without a second router tree — and makes any view
 * linkable and back-navigable.
 */

export function useUrlParam(key: string, fallback = ""): [string, (value: string) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? fallback;
  const set = useCallback(
    (next: string) => {
      setParams(
        (prev) => {
          const draft = new URLSearchParams(prev);
          if (next) draft.set(key, next);
          else draft.delete(key);
          return draft;
        },
        { replace: true },
      );
    },
    [key, setParams],
  );
  return [value, set];
}

export function useUrlFlag(key: string): [boolean, (on: boolean) => void] {
  const [value, set] = useUrlParam(key);
  return [value === "1", (on: boolean) => set(on ? "1" : "")];
}
